import CheckableFileList from "@/taskpane/components/checkable-file/CheckableFile";
import FolderPageLayout from "@/taskpane/modules/folder/FolderPageLayout";
import { UploadableFile } from "@/taskpane/types/file";
import { getAttachments } from "@/taskpane/utils/attachments";
import { SubFolder } from "@/taskpane/types/folder";
import { Card, Checkbox, Divider, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { HiMail } from "react-icons/hi";
import { useParams, useSearchParams } from "react-router-dom";
import { useOfficeContext } from "@/taskpane/contexts/office/office-context";
import { checkMimeTypeAllow } from "@/taskpane/hooks/file-dnd";
import { useGetSubFoldersQuery } from "@/taskpane/services/folders.hook";
import { GradientBorder } from "@/taskpane/components/styled-container/gradient-wrapper";
import GradientText from "@/taskpane/components/styled-container/GradientText";

interface SafeAttachment extends UploadableFile {
  contentType: string;
  type: string;
  lastModifiedDate?: Date;
  mimeTypeAllow: boolean;
  uploaded?: boolean;
}

type FileType = UploadableFile & { mimeTypeAllow: boolean; uploaded?: boolean };

/**
 * Creates a safe attachment object.
 *
 * @param attachment - The attachment object.
 * @param index - The index of the attachment.
 * @returns The safe attachment object.
 */
function createSafeAttachment(attachment: Partial<SafeAttachment>, index: number): SafeAttachment {
  const file = new File([attachment.base64 || ''], attachment.name || `Unnamed Attachment ${index}`, {
    type: attachment.contentType || attachment.type || "application/octet-stream"
  });


  return {
    id: attachment?.id || `attachment-${index}`,
    name: attachment?.name || `Unnamed Attachment ${index}`,
    size: attachment?.size || 0,
    contentType: attachment?.contentType || "application/octet-stream",
    base64: attachment?.base64 || "",
    type: attachment?.type || attachment?.contentType || "application/octet-stream",
    lastModifiedDate: attachment?.lastModifiedDate,
    mimeTypeAllow: checkMimeTypeAllow(file),
  };
}

/* global Office */

export default function FolderAttachmentsPage() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const sousDossierId = searchParams.get("sousDossierId");
  const [currentEmailId, setCurrentEmailId] = useState<string | undefined>(undefined);
  const [emailSubject, setEmailSubject] = useState<string>("");
  const { currentMailbox } = useOfficeContext();


  const updateCurrentEmail = useCallback(async () => {
    if (currentMailbox?.item) {
      const newEmailId = currentMailbox.item.itemId;
      const newSubject = currentMailbox.item.subject || "(No subject)";
      setCurrentEmailId(newEmailId);
      setEmailSubject(newSubject);
    }
  }, [setCurrentEmailId, setEmailSubject]);
  

  const {
    data: dataAttachments,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["getAttachments", currentEmailId],
    queryFn: getAttachments,
    enabled: !!currentEmailId,
  });

  const { data: subFolders, isLoading: subFoldersLoading } = useGetSubFoldersQuery(params.folderId || "", {
    enabled: !!params.folderId,
  });

  useEffect(() => {
    updateCurrentEmail();
    const intervalId = setInterval(() => {
      updateCurrentEmail();
    }, 5000); // Check for updates every 5 seconds
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (currentEmailId) {
      refetch();
    }
  }, [currentEmailId, refetch]);

  const [selectedSubFolder, setSelectedSubFolder] = useState<SubFolder>();
  const [selectedAttachments, setSelectedAttachments] = useState<SafeAttachment[]>([]);
  const [uploadedAttachments, setUploadedAttachments] = useState<SafeAttachment[]>([]);
  const [uploadLoading, setUploadLoading] = useState(false);

  const attachments = Array.isArray(dataAttachments?.attachments) ? dataAttachments.attachments : [];

  useEffect(() => {

    if (subFoldersLoading || !subFolders || selectedSubFolder) {
      return;
    }

    let folderToSelect: SubFolder | undefined;


    if (sousDossierId && subFolders.length === 1) {
      folderToSelect = subFolders[0];
    } else if (sousDossierId && subFolders.length > 1) {
      folderToSelect = subFolders.find((subFolder) => subFolder.sous_dossier_id === sousDossierId);
    }

    if (folderToSelect) {
      setSelectedSubFolder(folderToSelect);
    }
  }, [subFolders, sousDossierId, selectedSubFolder, subFoldersLoading]);

  const safeAttachments: SafeAttachment[] = useMemo(() => {
    return attachments
      .map((attachment, index) => {
        try {
          const safeAttachment = createSafeAttachment(attachment, index);
          // Check if this attachment has been uploaded
          const isUploaded = uploadedAttachments.some(uploaded => uploaded.id === safeAttachment.id);
          return {
            ...safeAttachment,
            uploaded: isUploaded
          };
        } catch (error) {
          console.error(`Error creating safe attachment for index ${index}:`, error);
          return null;
        }
      })
      .filter((attachment): attachment is SafeAttachment & { uploaded: boolean } => attachment !== null);
  }, [attachments, uploadedAttachments]);

  const handleSetSelectedAttachments = useCallback((attachments: React.SetStateAction<FileType[]>) => {
    if (typeof attachments === 'function') {
      setSelectedAttachments(prev => {
        const newState = attachments(prev as FileType[]);
        return (Array.isArray(newState) ? newState.filter(attachment => attachment.mimeTypeAllow) : []) as SafeAttachment[];
      });
    } else if (Array.isArray(attachments)) {
      const allowedAttachments = attachments.filter(attachment => attachment.mimeTypeAllow);
      setSelectedAttachments(allowedAttachments as SafeAttachment[]);
    } else {
      console.warn('Received invalid attachments:', attachments);
      setSelectedAttachments([]);
    }
  }, []);

  return (
    <FolderPageLayout
      folderId={params.folderId}
      onSelectSubFolder={setSelectedSubFolder}
      selectedSubfolder={selectedSubFolder}
      subFolders={subFolders}
      files={selectedAttachments as UploadableFile[]}
      canValidate={!!selectedSubFolder && selectedAttachments.length > 0}
      onValidate={() => {
        setUploadLoading(false);
        setUploadedAttachments([...uploadedAttachments, ...selectedAttachments]);
        setSelectedAttachments([]);
        setSelectedSubFolder(undefined);
      }}
      onUpload={() => {
        setUploadLoading(true);
      }}
    >
        <Divider sx={{
          height: "1px",
          backgroundColor: "#2563EB",
          marginTop: "-16px",
          width: "100%",
          marginBottom: "16px",
        }} />
      <Stack sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Typography fontWeight="bold" fontSize={14} color="#1C1D24" sx={{ flexShrink: 0 }}>
          <GradientText>
            Sélection des pièces jointes :
          </GradientText>
        </Typography>
        <GradientBorder>
        <Card
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            opacity: uploadLoading ? 0.5 : 1,
            pointerEvents: uploadLoading ? "none" : "auto",
          }}
        >
          <Stack gap={0.5}>
            {isLoading ? (
              <Typography textAlign="center" color="grey.400" p={3}>
                Chargement des pièces jointes...
              </Typography>
            ) : safeAttachments.length === 0 ? (
              <Stack alignItems="center" spacing={2} p={3}>
                <Typography textAlign="center" color="grey.400">
                  Aucune pièce jointe trouvée dans le mail sélectionné
                </Typography>
              </Stack>
            ) : (
              <>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.5}
                  onClick={() => {
                    const newSelectedAttachments = selectedAttachments.length > 0 ? [] : [...safeAttachments];
                    handleSetSelectedAttachments(newSelectedAttachments);
                  }}
                  sx={{
                    cursor: "pointer",
                  }}
                >
                  <Checkbox
                    sx={{
                      "& .MuiSvgIcon-root": {
                        borderRadius: "3px",
                      },
                    }}
                    size="small"
                    indeterminate={
                      selectedAttachments.length > 0 && selectedAttachments.length < safeAttachments.length
                    }
                    checked={selectedAttachments.length === safeAttachments.length}
                    color="primary"
                  />
                  <HiMail color="#1D7BF5" />
                  <Typography fontSize={12}>{emailSubject}</Typography>
                </Stack>
                <CheckableFileList
                  attachments={safeAttachments as (FileType & { uploaded?: boolean })[]}
                  selectedAttachments={selectedAttachments as FileType[]}
                  setSelectedAttachments={handleSetSelectedAttachments as React.Dispatch<React.SetStateAction<FileType[]>>}
                  disabled={uploadLoading}
                  loading={uploadLoading}
                  newItemIds={new Set()}
                />
              </>
            )}
          </Stack>
        </Card>
        </GradientBorder>
      </Stack>
    </FolderPageLayout>
  );
}
